import React, { useEffect, useState } from 'react';
import {
  PencilSquareIcon, TruckIcon, BoltIcon, ClockIcon, CheckCircleIcon,
  PlusIcon, XMarkIcon, SparklesIcon, ArchiveBoxXMarkIcon, ShoppingBagIcon, GiftIcon,
} from '@heroicons/react/24/outline';
import { useDelivery } from '../hooks/useDelivery.ts';

const BUILT_IN_TYPES = ['standard', 'express', 'collection'];

const Spinner = () => (
    <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
    </svg>
);

const formatCurrency = (cents: number) => `R${(cents / 100).toFixed(2)}`;
const randsToZents  = (value: string) => Math.round((parseFloat(value) || 0) * 100);

const FIELD = 'w-full rounded-lg border border-stone-300 px-3 py-2.5 text-sm focus:ring-2 focus:ring-[#ee2b74] outline-none';
const FIELD_CURRENCY = 'w-full rounded-lg border border-stone-300 pl-8 pr-3 py-2.5 text-sm focus:ring-2 focus:ring-[#ee2b74] outline-none';

export default function DeliveryFeesPage() {
  const {
    options, freeDeliveryConfig, loading,
    fetchAll, updateFees, createOption, archiveOption,
    saveFreeDeliveryThreshold, toggleFreeDelivery,
  } = useDelivery();

  const standardFee   = options.find(o => o.type === 'standard') ?? null;
  const expressFee    = options.find(o => o.type === 'express')  ?? null;
  const collectionFee = options.find(o => o.type === 'collection') ?? null;
  const customOptions = options.filter(o => !BUILT_IN_TYPES.includes(o.type));

  useEffect(() => { void fetchAll(); }, [fetchAll]);

  // ── Update fees modal ──────────────────────────────────────────────────────
  const [isUpdateModalOpen, setIsUpdateModalOpen] = useState(false);
  const [updateForm, setUpdateForm] = useState({ standard_amount: 0, express_amount: 0 });
  const [saving, setSaving] = useState(false);
  const [updateSuccess, setUpdateSuccess] = useState('');

  const openUpdateModal = () => {
    setUpdateForm({ standard_amount: standardFee?.amount ?? 0, express_amount: expressFee?.amount ?? 0 });
    setUpdateSuccess('');
    setIsUpdateModalOpen(true);
  };
  const closeUpdateModal = () => { setIsUpdateModalOpen(false); setUpdateSuccess(''); };

  const submitUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      const now = new Date().toISOString();
      const newPrices: { type: string; amount: number; valid_from: string }[] = [];
      if (Math.round(updateForm.standard_amount) !== standardFee?.amount)
        newPrices.push({ type: 'standard', amount: Math.round(updateForm.standard_amount), valid_from: now });
      if (Math.round(updateForm.express_amount) !== expressFee?.amount)
        newPrices.push({ type: 'express', amount: Math.round(updateForm.express_amount), valid_from: now });
      if (newPrices.length === 0) {
        setUpdateSuccess('No changes detected.');
        setTimeout(closeUpdateModal, 1000);
        return;
      }
      await updateFees(newPrices);
      setUpdateSuccess('Delivery fees updated successfully!');
      setTimeout(closeUpdateModal, 1500);
    } catch (err) {
      alert(`Update failed: ${err instanceof Error ? err.message : 'Something went wrong'}`);
    } finally {
      setSaving(false);
    }
  };

  // ── Create option modal ────────────────────────────────────────────────────
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [createForm, setCreateForm] = useState({ type: '', amount: 0, valid_to: '' });
  const [creating, setCreating] = useState(false);
  const [createSuccess, setCreateSuccess] = useState('');

  const openCreateModal = () => { setCreateForm({ type: '', amount: 0, valid_to: '' }); setCreateSuccess(''); setIsCreateModalOpen(true); };
  const closeCreateModal = () => { setIsCreateModalOpen(false); setCreateSuccess(''); };

  const submitCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    setCreating(true);
    try {
      const slug = createForm.type.trim().toLowerCase().replace(/\s+/g, '_');
      const valid_to = createForm.valid_to
          ? new Date(`${createForm.valid_to}T23:59:59`).toISOString()
          : undefined;
      await createOption(slug, createForm.amount, valid_to);
      setCreateSuccess('Option created successfully!');
      setTimeout(closeCreateModal, 1500);
    } catch (err) {
      alert(`Failed to create option: ${err instanceof Error ? err.message : 'Something went wrong'}`);
    } finally {
      setCreating(false);
    }
  };

  // ── Archive ────────────────────────────────────────────────────────────────
  const [archivingId, setArchivingId] = useState<string | null>(null);

  const handleArchive = async (option: typeof options[0]) => {
    if (!window.confirm(`Archive "${option.type.replace(/_/g, ' ')}"? It will no longer be selectable for new orders.`)) return;
    setArchivingId(option.id);
    try {
      await archiveOption(option.id);
    } catch (err) {
      alert(`Archive failed: ${err instanceof Error ? err.message : 'Something went wrong'}`);
    } finally {
      setArchivingId(null);
    }
  };

  // ── Enable collection ──────────────────────────────────────────────────────
  const handleEnableCollection = async () => {
    if (!window.confirm('Enable collection? This will add a free collection option for customers.')) return;
    try {
      await createOption('collection', 0);
    } catch (err) {
      alert(`Failed: ${err instanceof Error ? err.message : 'Something went wrong'}`);
    }
  };

  // ── Free delivery modal ────────────────────────────────────────────────────
  const [isFreeDeliveryModalOpen, setIsFreeDeliveryModalOpen] = useState(false);
  const [freeDeliveryForm, setFreeDeliveryForm] = useState({ threshold: '0.00' });
  const [savingFreeDelivery, setSavingFreeDelivery] = useState(false);
  const [freeDeliverySuccess, setFreeDeliverySuccess] = useState('');

  const openFreeDeliveryModal = () => {
    setFreeDeliveryForm({ threshold: freeDeliveryConfig ? (freeDeliveryConfig.threshold_cents / 100).toFixed(2) : '0.00' });
    setFreeDeliverySuccess('');
    setIsFreeDeliveryModalOpen(true);
  };
  const closeFreeDeliveryModal = () => { setIsFreeDeliveryModalOpen(false); setFreeDeliverySuccess(''); };

  const submitFreeDelivery = async (e: React.FormEvent) => {
    e.preventDefault();
    setSavingFreeDelivery(true);
    try {
      await saveFreeDeliveryThreshold(randsToZents(freeDeliveryForm.threshold));
      setFreeDeliverySuccess('Free delivery threshold updated!');
      setTimeout(closeFreeDeliveryModal, 1500);
    } catch (err) {
      alert(`Failed to update threshold: ${err instanceof Error ? err.message : 'Something went wrong'}`);
    } finally {
      setSavingFreeDelivery(false);
    }
  };

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
      <div>
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <h2 className="text-2xl font-semibold text-stone-900">Delivery Fees</h2>
            <p className="text-sm text-stone-500 mt-1">Manage delivery pricing for standard, express and custom options</p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={openCreateModal} disabled={loading}
                    className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg border border-stone-300 bg-white text-stone-700 text-sm font-medium hover:bg-stone-50 transition disabled:opacity-50">
              <PlusIcon className="h-4 w-4" /> New Option
            </button>
            <button onClick={openUpdateModal} disabled={loading}
                    className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-[#ee2b74] text-white text-sm font-medium hover:bg-[#d4245f] transition disabled:opacity-50">
              <PencilSquareIcon className="h-4 w-4" /> Update Fees
            </button>
          </div>
        </div>

        {loading ? (
            <div className="py-12 text-center text-stone-400 text-sm">Loading delivery fees…</div>
        ) : (
            <div className="space-y-6">
              {/* Standard, Express & Collection */}
              <div className="grid md:grid-cols-3 gap-4">
                {/* Standard */}
                <div className="rounded-xl border border-stone-200 bg-white shadow-sm overflow-hidden">
                  <div className="h-1 bg-[#f5ab40] w-full" />
                  <div className="p-6">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="h-12 w-12 rounded-xl bg-[#fff8ec] flex items-center justify-center">
                        <TruckIcon className="h-6 w-6 text-[#f5ab40]" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-stone-900 text-lg">Standard Delivery</h3>
                        <p className="text-xs text-stone-500 flex items-center gap-1 mt-1">
                          <ClockIcon className="h-3.5 w-3.5" /> Regular delivery service
                        </p>
                      </div>
                    </div>
                    <div className="pt-4 border-t border-stone-100">
                      <div className="flex items-baseline gap-2">
                                        <span className="text-3xl font-bold text-stone-900">
                                            {standardFee ? formatCurrency(standardFee.amount) : 'N/A'}
                                        </span>
                        <span className="text-sm text-stone-500">per delivery</span>
                      </div>
                      {standardFee && (
                          <div className="mt-3 flex items-center gap-2 text-xs text-stone-500">
                            <CheckCircleIcon className="h-4 w-4 text-[#8dc63f]" />
                            Active since {new Date(standardFee.valid_from).toLocaleDateString()}
                          </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Express */}
                <div className="rounded-xl border border-stone-200 bg-white shadow-sm overflow-hidden">
                  <div className="h-1 bg-[#ee2b74] w-full" />
                  <div className="p-6">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="h-12 w-12 rounded-xl bg-pink-50 flex items-center justify-center">
                        <BoltIcon className="h-6 w-6 text-[#ee2b74]" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-stone-900 text-lg">Express Delivery</h3>
                        <p className="text-xs text-stone-500 flex items-center gap-1 mt-1">
                          <BoltIcon className="h-3.5 w-3.5" /> Fast-track delivery service
                        </p>
                      </div>
                    </div>
                    <div className="pt-4 border-t border-stone-100">
                      <div className="flex items-baseline gap-2">
                                        <span className="text-3xl font-bold text-stone-900">
                                            {expressFee ? formatCurrency(expressFee.amount) : 'N/A'}
                                        </span>
                        <span className="text-sm text-stone-500">per delivery</span>
                      </div>
                      {expressFee && (
                          <div className="mt-3 flex items-center gap-2 text-xs text-stone-500">
                            <CheckCircleIcon className="h-4 w-4 text-[#8dc63f]" />
                            Active since {new Date(expressFee.valid_from).toLocaleDateString()}
                          </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Collection */}
                <div className="rounded-xl border border-stone-200 bg-white shadow-sm overflow-hidden">
                  <div className="h-1 bg-[#8dc63f] w-full" />
                  <div className="p-6">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="h-12 w-12 rounded-xl bg-[#f0f7e6] flex items-center justify-center">
                        <ShoppingBagIcon className="h-6 w-6 text-[#8dc63f]" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-stone-900 text-lg">Collection</h3>
                        <p className="text-xs text-stone-500 flex items-center gap-1 mt-1">
                          <ShoppingBagIcon className="h-3.5 w-3.5" /> Customer collects in store
                        </p>
                      </div>
                    </div>
                    <div className="pt-4 border-t border-stone-100">
                      {collectionFee ? (
                          <>
                            <div className="flex items-baseline gap-2">
                                                <span className="text-3xl font-bold text-stone-900">
                                                    {formatCurrency(collectionFee.amount)}
                                                </span>
                              <span className="text-sm text-stone-500">per collection</span>
                            </div>
                            <div className="mt-3 flex items-center gap-2 text-xs text-stone-500">
                              <CheckCircleIcon className="h-4 w-4 text-[#8dc63f]" />
                              Active since {new Date(collectionFee.valid_from).toLocaleDateString()}
                            </div>
                          </>
                      ) : (
                          <>
                            <p className="text-sm text-stone-400 mb-3">Not enabled yet.</p>
                            <button onClick={handleEnableCollection}
                                    className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-[#8dc63f] text-white text-xs font-semibold hover:bg-[#7aaa35] transition">
                              <PlusIcon className="h-3.5 w-3.5" /> Enable Collection
                            </button>
                          </>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Free Delivery Threshold */}
              <div>
                <h3 className="text-xs font-semibold text-stone-500 uppercase tracking-widest mb-3">Free Delivery Threshold</h3>
                <div className="rounded-xl border border-stone-200 bg-white shadow-sm overflow-hidden">
                  <div className="h-1 bg-[#8dc63f] w-full" />
                  <div className="p-6">
                    <div className="flex items-start justify-between gap-4 flex-wrap">
                      <div className="flex items-center gap-4">
                        <div className="h-12 w-12 rounded-xl bg-[#f0f7e6] flex items-center justify-center flex-shrink-0">
                          <GiftIcon className="h-6 w-6 text-[#8dc63f]" />
                        </div>
                        <div>
                          <h3 className="font-semibold text-stone-900 text-lg">Free Delivery</h3>
                          <p className="text-xs text-stone-500 mt-1">Customers unlock free delivery when their cart reaches the threshold</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3 flex-shrink-0">
                        {freeDeliveryConfig && (
                            <button onClick={toggleFreeDelivery}
                                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${freeDeliveryConfig.is_active ? 'bg-[#8dc63f]' : 'bg-stone-300'}`}
                                    title={freeDeliveryConfig.is_active ? 'Disable free delivery' : 'Enable free delivery'}>
                              <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${freeDeliveryConfig.is_active ? 'translate-x-6' : 'translate-x-1'}`} />
                            </button>
                        )}
                        <button onClick={openFreeDeliveryModal}
                                className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-stone-300 bg-white text-stone-700 text-xs font-semibold hover:bg-stone-50 transition">
                          <PencilSquareIcon className="h-3.5 w-3.5" />
                          {freeDeliveryConfig ? 'Edit Threshold' : 'Set Threshold'}
                        </button>
                      </div>
                    </div>
                    <div className="mt-5 pt-4 border-t border-stone-100">
                      {freeDeliveryConfig ? (
                          <div className="flex items-center gap-6 flex-wrap">
                            <div>
                              <p className="text-xs text-stone-400 uppercase font-medium tracking-wide mb-1">Threshold</p>
                              <p className="text-3xl font-bold text-stone-900">{formatCurrency(freeDeliveryConfig.threshold_cents)}</p>
                            </div>
                            <div>
                              <p className="text-xs text-stone-400 uppercase font-medium tracking-wide mb-1">Status</p>
                              <span className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold ${freeDeliveryConfig.is_active ? 'bg-[#f0f7e6] text-[#5a7f28] ring-1 ring-[#c5e08a]' : 'bg-stone-100 text-stone-500 ring-1 ring-stone-200'}`}>
                                                    <span className={`h-1.5 w-1.5 rounded-full ${freeDeliveryConfig.is_active ? 'bg-[#8dc63f]' : 'bg-stone-400'}`} />
                                {freeDeliveryConfig.is_active ? 'Active' : 'Disabled'}
                                                </span>
                            </div>
                            <div className="text-xs text-stone-400 italic">
                              When a customer's cart reaches {formatCurrency(freeDeliveryConfig.threshold_cents)}, the free delivery option unlocks at checkout.
                            </div>
                          </div>
                      ) : (
                          <div className="flex items-center gap-3">
                            <p className="text-sm text-stone-400 italic">No threshold configured yet.</p>
                            <button onClick={openFreeDeliveryModal}
                                    className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-[#8dc63f] text-white text-xs font-semibold hover:bg-[#7aaa35] transition">
                              <PlusIcon className="h-3.5 w-3.5" /> Set Threshold
                            </button>
                          </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Custom / Special options */}
              {customOptions.length > 0 && (
                  <div>
                    <h3 className="text-xs font-semibold text-stone-500 uppercase tracking-widest mb-3">Special / Custom Options</h3>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                      {customOptions.map(option => (
                          <div key={option.id} className="rounded-xl border border-stone-200 bg-white shadow-sm overflow-hidden">
                            <div className="p-5">
                              <div className="flex items-start justify-between mb-3">
                                <div className="flex items-center gap-3">
                                  <div className="h-10 w-10 rounded-xl bg-[#fff8ec] flex items-center justify-center">
                                    <SparklesIcon className="h-5 w-5 text-[#f5ab40]" />
                                  </div>
                                  <div>
                                    <h4 className="font-semibold text-stone-900 capitalize">{option.type.replace(/_/g, ' ')}</h4>
                                    <p className="text-xs text-stone-400 mt-0.5">Custom option</p>
                                  </div>
                                </div>
                                <button onClick={() => handleArchive(option)} disabled={archivingId === option.id}
                                        title="Archive this option"
                                        className="p-1.5 rounded-lg text-stone-400 hover:text-red-500 hover:bg-red-50 transition disabled:opacity-50">
                                  {archivingId === option.id ? <Spinner /> : <ArchiveBoxXMarkIcon className="h-4 w-4" />}
                                </button>
                              </div>
                              <div className="pt-3 border-t border-stone-100">
                                <div className="flex items-baseline gap-2">
                                  <span className="text-2xl font-bold text-stone-900">{formatCurrency(option.amount)}</span>
                                  <span className="text-xs text-stone-500">per delivery</span>
                                </div>
                                <div className="mt-2 flex items-center gap-2 text-xs text-stone-500">
                                  <CheckCircleIcon className="h-3.5 w-3.5 text-[#8dc63f]" />
                                  {option.valid_to
                                      ? `Expires ${new Date(option.valid_to).toLocaleDateString()}`
                                      : `Active since ${new Date(option.valid_from).toLocaleDateString()}`}
                                </div>
                              </div>
                            </div>
                          </div>
                      ))}
                    </div>
                  </div>
              )}
            </div>
        )}

        {/* Update fees modal */}
        {isUpdateModalOpen && (
            <div className="fixed inset-0 flex items-center justify-center z-50 bg-black/30 backdrop-blur-sm p-4">
              <div className="bg-white rounded-2xl shadow-lg w-full max-w-md overflow-hidden">
                <div className="h-1 bg-[#ee2b74] w-full" />
                <div className="p-6">
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <h3 className="text-lg font-semibold text-stone-900">Update Delivery Fees</h3>
                      <p className="text-xs text-stone-500 mt-0.5">Set new standard and express fees</p>
                    </div>
                    <button onClick={closeUpdateModal} className="p-1 rounded-lg text-stone-400 hover:text-stone-600 hover:bg-stone-100">
                      <XMarkIcon className="h-5 w-5" />
                    </button>
                  </div>
                  {updateSuccess && (
                      <div className="mt-4 flex items-center gap-2 p-3 rounded-lg bg-green-50 text-green-700 text-sm">
                        <CheckCircleIcon className="h-5 w-5 flex-shrink-0" />{updateSuccess}
                      </div>
                  )}
                  <form onSubmit={submitUpdate} className="mt-5 space-y-5">
                    <div>
                      <label className="block text-sm font-semibold text-stone-700 mb-2">
                        <div className="flex items-center gap-2"><TruckIcon className="h-4 w-4 text-stone-500" /> Standard Delivery Fee (R)</div>
                      </label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-500 text-sm">R</span>
                        <input type="number" step="0.01" min="0" required placeholder="0.00"
                               className={FIELD_CURRENCY}
                               value={(updateForm.standard_amount / 100).toFixed(2)}
                               onChange={e => setUpdateForm(p => ({ ...p, standard_amount: randsToZents(e.target.value) }))} />
                      </div>
                      <p className="mt-1.5 text-xs text-stone-500">Current: {formatCurrency(standardFee?.amount ?? 0)}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-stone-700 mb-2">
                        <div className="flex items-center gap-2"><BoltIcon className="h-4 w-4 text-[#ee2b74]" /> Express Delivery Fee (R)</div>
                      </label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-500 text-sm">R</span>
                        <input type="number" step="0.01" min="0" required placeholder="0.00"
                               className={FIELD_CURRENCY}
                               value={(updateForm.express_amount / 100).toFixed(2)}
                               onChange={e => setUpdateForm(p => ({ ...p, express_amount: randsToZents(e.target.value) }))} />
                      </div>
                      <p className="mt-1.5 text-xs text-stone-500">Current: {formatCurrency(expressFee?.amount ?? 0)}</p>
                    </div>
                    <div className="rounded-lg border border-[#ee2b74] bg-pink-50/30 p-3 text-xs text-[#ee2b74]">
                      <div className="flex gap-2"><ClockIcon className="h-4 w-4 flex-shrink-0 mt-0.5" />
                        <p><strong>Note:</strong> Previous fees will be archived. New fees become active immediately.</p>
                      </div>
                    </div>
                    <div className="flex justify-end gap-3 pt-4 border-t border-stone-100">
                      <button type="button" onClick={closeUpdateModal} disabled={saving}
                              className="px-4 py-2.5 rounded-lg border border-stone-300 text-stone-700 text-sm font-medium hover:bg-stone-50 disabled:opacity-50">Cancel</button>
                      <button type="submit" disabled={saving || !!updateSuccess}
                              className="px-4 py-2.5 rounded-lg bg-[#ee2b74] text-white text-sm font-medium hover:bg-[#d4245f] disabled:opacity-60 flex items-center gap-2">
                        {saving ? <><Spinner /> Updating…</> : updateSuccess ? <><CheckCircleIcon className="h-4 w-4" /> Updated!</> : 'Update Fees'}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
        )}

        {/* Create custom option modal */}
        {isCreateModalOpen && (
            <div className="fixed inset-0 flex items-center justify-center z-50 bg-black/30 backdrop-blur-sm p-4">
              <div className="bg-white rounded-2xl shadow-lg w-full max-w-md overflow-hidden">
                <div className="h-1 bg-[#ee2b74] w-full" />
                <div className="p-6">
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <h3 className="text-lg font-semibold text-stone-900">Create Delivery Option</h3>
                      <p className="text-xs text-stone-500 mt-0.5">Add a custom option such as a free delivery special</p>
                    </div>
                    <button onClick={closeCreateModal} className="p-1 rounded-lg text-stone-400 hover:text-stone-600 hover:bg-stone-100">
                      <XMarkIcon className="h-5 w-5" />
                    </button>
                  </div>
                  {createSuccess && (
                      <div className="mt-4 flex items-center gap-2 p-3 rounded-lg bg-green-50 text-green-700 text-sm">
                        <CheckCircleIcon className="h-5 w-5 flex-shrink-0" />{createSuccess}
                      </div>
                  )}
                  <form onSubmit={submitCreate} className="mt-5 space-y-5">
                    <div>
                      <label className="block text-sm font-semibold text-stone-700 mb-2">Option Name</label>
                      <input type="text" required placeholder="e.g. Free Delivery Special"
                             className={FIELD} value={createForm.type}
                             onChange={e => setCreateForm(p => ({ ...p, type: e.target.value }))} />
                      <p className="mt-1 text-xs text-stone-400">Spaces are stored as underscores (e.g. <span className="font-mono">free_delivery_special</span>)</p>
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-stone-700 mb-2">Delivery Fee (R)</label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-500 text-sm">R</span>
                        <input type="number" step="0.01" min="0" required placeholder="0.00"
                               className={FIELD_CURRENCY}
                               value={(createForm.amount / 100).toFixed(2)}
                               onChange={e => setCreateForm(p => ({ ...p, amount: randsToZents(e.target.value) }))} />
                      </div>
                      <p className="mt-1 text-xs text-stone-400">Set to R0.00 for a free delivery option</p>
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-stone-700 mb-2">
                        Expiry Date <span className="text-stone-400 font-normal">(optional)</span>
                      </label>
                      <input type="date" className={FIELD} value={createForm.valid_to}
                             min={new Date().toISOString().split('T')[0]}
                             onChange={e => setCreateForm(p => ({ ...p, valid_to: e.target.value }))} />
                      <p className="mt-1 text-xs text-stone-400">Leave blank to keep active until manually archived</p>
                    </div>
                    <div className="rounded-lg border border-[#ee2b74] bg-pink-50/30 p-3 text-xs text-[#ee2b74]">
                      <div className="flex gap-2"><SparklesIcon className="h-4 w-4 flex-shrink-0 mt-0.5" />
                        <p><strong>Tip:</strong> Once created, this option will appear when creating new orders. You can remove it at any time using the archive button.</p>
                      </div>
                    </div>
                    <div className="flex justify-end gap-3 pt-4 border-t border-stone-100">
                      <button type="button" onClick={closeCreateModal} disabled={creating}
                              className="px-4 py-2.5 rounded-lg border border-stone-300 text-stone-700 text-sm font-medium hover:bg-stone-50 disabled:opacity-50">Cancel</button>
                      <button type="submit" disabled={creating || !!createSuccess}
                              className="px-4 py-2.5 rounded-lg bg-[#ee2b74] text-white text-sm font-medium hover:bg-[#d4245f] disabled:opacity-60 flex items-center gap-2">
                        {creating ? <><Spinner /> Creating…</> : createSuccess ? <><CheckCircleIcon className="h-4 w-4" /> Created!</> : <><PlusIcon className="h-4 w-4" /> Create Option</>}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
        )}

        {/* Free delivery threshold modal */}
        {isFreeDeliveryModalOpen && (
            <div className="fixed inset-0 flex items-center justify-center z-50 bg-black/30 backdrop-blur-sm p-4">
              <div className="bg-white rounded-2xl shadow-lg w-full max-w-md overflow-hidden">
                <div className="h-1 bg-[#8dc63f] w-full" />
                <div className="p-6">
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <h3 className="text-lg font-semibold text-stone-900">Free Delivery Threshold</h3>
                      <p className="text-xs text-stone-500 mt-0.5">Set the minimum cart value to unlock free delivery</p>
                    </div>
                    <button onClick={closeFreeDeliveryModal} className="p-1 rounded-lg text-stone-400 hover:text-stone-600 hover:bg-stone-100">
                      <XMarkIcon className="h-5 w-5" />
                    </button>
                  </div>
                  {freeDeliverySuccess && (
                      <div className="mt-4 flex items-center gap-2 p-3 rounded-lg bg-green-50 text-green-700 text-sm">
                        <CheckCircleIcon className="h-5 w-5 flex-shrink-0" />{freeDeliverySuccess}
                      </div>
                  )}
                  <form onSubmit={submitFreeDelivery} className="mt-5 space-y-5">
                    <div>
                      <label className="block text-sm font-semibold text-stone-700 mb-2">Minimum Cart Value (R)</label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-500 text-sm">R</span>
                        <input type="number" step="0.01" min="0" required placeholder="0.00"
                               className="w-full rounded-lg border border-stone-300 pl-8 pr-3 py-2.5 text-sm focus:ring-2 focus:ring-[#8dc63f] outline-none"
                               value={freeDeliveryForm.threshold}
                               onChange={e => setFreeDeliveryForm({ threshold: e.target.value })} />
                      </div>
                      <p className="mt-1.5 text-xs text-stone-500">
                        Customers will see a progress indicator in the app. Once their cart reaches this value, the free delivery option unlocks.
                      </p>
                    </div>
                    <div className="rounded-lg border border-[#c5e08a] bg-[#f0f7e6] p-3 text-xs text-[#5a7f28]">
                      <div className="flex gap-2"><GiftIcon className="h-4 w-4 flex-shrink-0 mt-0.5" />
                        <p><strong>How it works:</strong> The free delivery option is locked until the customer's cart total meets this threshold. Express and paid options remain available regardless.</p>
                      </div>
                    </div>
                    <div className="flex justify-end gap-3 pt-4 border-t border-stone-100">
                      <button type="button" onClick={closeFreeDeliveryModal} disabled={savingFreeDelivery}
                              className="px-4 py-2.5 rounded-lg border border-stone-300 text-stone-700 text-sm font-medium hover:bg-stone-50 disabled:opacity-50">Cancel</button>
                      <button type="submit" disabled={savingFreeDelivery || !!freeDeliverySuccess}
                              className="px-4 py-2.5 rounded-lg bg-[#8dc63f] text-white text-sm font-medium hover:bg-[#7aaa35] disabled:opacity-60 flex items-center gap-2">
                        {savingFreeDelivery ? <><Spinner /> Saving…</> : freeDeliverySuccess ? <><CheckCircleIcon className="h-4 w-4" /> Saved!</> : 'Save Threshold'}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
        )}
      </div>
  );
}